import React, { useEffect, useId } from 'react';
import { useCloseOnBack } from '../hooks/useCloseOnBack';
import { useTranslation } from '../i18n';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Visible heading; omit for chrome-less dialogs (e.g. inline login). */
  title?: React.ReactNode;
  /** Use when `title` is omitted so screen readers get a dialog name. */
  ariaLabel?: string;
  /** Extra classes on the inner panel (e.g. `modal--wide modal--scroll`). */
  panelClassName?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  /** Close on browser/device back (Android back, trackpad swipe back). */
  closeOnBack?: boolean;
  /** Top-right dismiss control (always shown when `title` is set). */
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  children,
  title,
  ariaLabel,
  panelClassName = '',
  closeOnOverlayClick = false,
  closeOnEscape = true,
  closeOnBack = true,
  showCloseButton = true,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const hasTitle = title != null && title !== '';

  useCloseOnBack(open, onClose, closeOnBack);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const closeLabel = t('ui.modal_close');
  const panelClasses = [
    'modal',
    panelClassName,
    hasTitle ? 'modal--has-title' : '',
    !hasTitle && showCloseButton ? 'modal--floating-close' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        className={panelClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-label={!hasTitle ? ariaLabel : undefined}
        onClick={e => e.stopPropagation()}
      >
        {hasTitle && (
          <div className="modal__header">
            <h2 className="modal__title" id={titleId}>
              {title}
            </h2>
            {showCloseButton && (
              <button
                type="button"
                className="modal__close"
                onClick={onClose}
                aria-label={closeLabel}
              >
                &times;
              </button>
            )}
          </div>
        )}
        {!hasTitle && showCloseButton && (
          <button
            type="button"
            className="modal__close modal__close--floating"
            onClick={onClose}
            aria-label={closeLabel}
          >
            &times;
          </button>
        )}
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
};
